import React from 'react';
import Responsive from '@amzn/meridian/responsive';
import { responsiveProps } from 'src/constants/responsiveProps';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import chevronLeftSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-left-small';
import Text from '@amzn/meridian/text';
import MultiContainerDetails from 'src/components/common/multi-container-details/MultiContainerDetails';
import ContainerDetails from 'src/components/common/container-details/ContainerDetails';
import BarcodeScan from 'src/components/common/barcode-scan/BarcodeScan';
import { ContainerDetailsModel } from 'src/components/common/container-details/ContainerDetailsModel';
import {
    BatchMoveContainersInput,
    buildBatchMoveContainersInput,
    MoveContainerMode
} from 'src/models/batchMoveContainers';
import { isNotEmpty } from 'src/utils/object-utils';
import { UIAlertState } from 'src/store/common-slices/uiSlice';
import { AlertLevels } from 'src/models/alert-model';
import { BatchMoveContainersMode } from 'src/store/common-slices/batchMoveContainersSlice';
import './DestinationContainerScan.scss';
import { SortContainerPages } from "src/store/common-slices/sortContainerSlice";

interface DestinationContainerScanPageProps {
    pageTitle: string;
    barcodeScanIcon: string;
    barcodeScanText: string;
    barcodeScanInputLabel: string;
    barcodeScanInputPlaceholder: string;
    barcodeScanButtonText: string;
    barcodeScanButtonIcon: React.ReactElement;
    headingTestId?: string;
    inputTestId?: string;
    buttonTestId?: string;
    moveContainerMode: MoveContainerMode;
    onBackButtonClick: () => void;
}

interface DestinationContainerScanStateProps {
    childContainers: ContainerDetailsModel[];
    destinationContainerId: string;
    batchMoveMode:  BatchMoveContainersMode;
    uiAlertState: UIAlertState;
    warehouseId: string,
    associate: string,
    locale: string
}

interface DestinationContainerScanDispatchProps {
    updateDestinationContainerId: (destinationContainerId: string) => void;
    batchMoveContainers: (batchMoveContainersInput: BatchMoveContainersInput) => void;
    resetChildAndDestinationContainers: () => void;
    resetBatchMovePageToRender: () => void;
    setSortationPageToRender: (pageToRender: SortContainerPages) => void;
}

type Props = DestinationContainerScanPageProps & DestinationContainerScanStateProps & DestinationContainerScanDispatchProps;

class DestinationContainerScanPage extends React.Component<Props, {}> {

    private onDestinationSubmit = async (containerId: string) => {
        await this.props.updateDestinationContainerId(containerId);
        await this.props.batchMoveContainers(buildBatchMoveContainersInput(this.props.destinationContainerId, 
            this.props.childContainers, this.props.moveContainerMode,
            this.props.warehouseId, this.props.associate, this.props.locale));
        if (this.props.uiAlertState.alertLevel === AlertLevels.SUCCESS ||
            this.props.uiAlertState.alertLevel === AlertLevels.WARNING) {
            this.props.resetBatchMovePageToRender();
            this.props.resetChildAndDestinationContainers();
            this.props.setSortationPageToRender(SortContainerPages.SCAN_SOURCE_CONTAINER_PAGE);
        }
    }

    public render() {
        return (
            <Responsive query={"max-width"} props={responsiveProps}>
                {props => (
                    <>
                        <div className={"destination-container-header"} data-testid="destination-container-scan-page">
                            <Button
                                type={"secondary"}
                                onClick={() => this.props.onBackButtonClick()}
                                data-testid="back-button"
                                circular={true}
                            >
                                <Icon tokens={chevronLeftSmallTokens} />
                            </Button>
                            <Text type={props.titleSize}>{this.props.pageTitle}</Text>
                        </div>

                        {isNotEmpty(this.props.childContainers) && (
                            this.props.batchMoveMode === BatchMoveContainersMode.SINGLE_MODE
                                ? <ContainerDetails containerDetails={this.props.childContainers[0]}/>
                                : <MultiContainerDetails childContainers={this.props.childContainers} mode={this.props.moveContainerMode}/>
                        )}

                        <BarcodeScan
                            shouldRenderImageOrIcon={!props.isScanner}
                            scanImageOrIcon={this.props.barcodeScanIcon}
                            scanImageAltText={"Destination scan Icon"}
                            shouldRenderText={!props.isScanner}
                            scanText={this.props.barcodeScanText}
                            inputLabel={this.props.barcodeScanInputLabel}
                            inputPlaceholder={this.props.barcodeScanInputPlaceholder}
                            shouldRenderButton={true}
                            buttonText={this.props.barcodeScanButtonText}
                            buttonIcon={this.props.barcodeScanButtonIcon}
                            onSubmit={this.onDestinationSubmit}
                            headingTestId={this.props.headingTestId}
                            inputTestId={this.props.inputTestId}
                            buttonTestId={this.props.buttonTestId}
                        />
                    </>
                )}
            </Responsive>
        )
    }
}

export default DestinationContainerScanPage;